import type { RefObject } from 'react'
import { ActionIcon, Alert, Box, Button, Card, Group, Stack, Text, Tooltip } from '@mantine/core'
import {
  IconEye,
  IconEyeOff,
  IconExternalLink,
  IconHistory,
  IconInbox,
  IconRefresh,
} from '@/components/icons'
import { TicketChatHeaderTagsRow } from '@/components/tickets/TicketDetailSidePanels'
import { TicketSpamControl } from '@/components/tickets/TicketSpamControl'
import {
  MessageBubble,
  isAssignmentSystemPseudoMessage,
  resolveQuotedForMessage,
} from '@/components/tickets/TicketMessageThread'
import type {
  ProjectTagRow,
  TicketDetailMessage,
  TicketDetailTicket,
  TicketSpamState,
  TicketTimelineItem,
  WorkspaceUserRow,
} from '@/types/api'

type WidgetEmailWaitState = {
  active: boolean
  replyTo: string
}

type TicketMessagesPanelProps = {
  ticket: TicketDetailTicket
  messages: TicketDetailMessage[]
  messagesTotal: number
  visibleMessages: TicketDetailMessage[]
  messageById: Map<string, TicketDetailMessage>
  users: WorkspaceUserRow[]
  currentUserId?: string
  tags: ProjectTagRow[]
  tagsToAdd: ProjectTagRow[]
  isPhoneLayout: boolean
  isTicketWideLayout: boolean
  loading: boolean
  threadRefreshing: boolean
  timelineLoading: boolean
  showAssignmentSystemMessages: boolean
  assignmentTimelineLoading: boolean
  assignmentTimelineError: string | null
  assignmentTimelineItems: TicketTimelineItem[]
  widgetEmailWait: WidgetEmailWaitState
  scrollViewport: RefObject<HTMLDivElement | null>
  messagesEndRef: RefObject<HTMLDivElement | null>
  onRemoveTag: (tagId: string) => void
  onAddTag: (tagId: string) => void
  onToggleSystemMessages: () => void
  onSpamChange: (state: TicketSpamState) => void
  onOpenTimeline: () => void
  onOpenTicketInNewTab: () => void
  onRefreshThread: () => void
  onLoadOlder: () => void
  onQuoteMessage: (message: TicketDetailMessage) => void
}

export function TicketMessagesPanel({
  ticket,
  messages,
  messagesTotal,
  visibleMessages,
  messageById,
  users,
  currentUserId,
  tags,
  tagsToAdd,
  isPhoneLayout,
  isTicketWideLayout,
  loading,
  threadRefreshing,
  timelineLoading,
  showAssignmentSystemMessages,
  assignmentTimelineLoading,
  assignmentTimelineError,
  assignmentTimelineItems,
  widgetEmailWait,
  scrollViewport,
  messagesEndRef,
  onRemoveTag,
  onAddTag,
  onToggleSystemMessages,
  onSpamChange,
  onOpenTimeline,
  onOpenTicketInNewTab,
  onRefreshThread,
  onLoadOlder,
  onQuoteMessage,
}: TicketMessagesPanelProps) {
  return (
    <Card
      withBorder
      padding={0}
      radius="md"
      style={{
        flex: isPhoneLayout ? '0 0 auto' : '1 1 0%',
        minHeight: isPhoneLayout ? undefined : 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: isPhoneLayout ? 'visible' : 'hidden',
        flexShrink: isPhoneLayout ? 0 : undefined,
      }}
    >
      <Box
        px="sm"
        py={6}
        style={{
          borderBottom: '1px solid var(--app-shell-border-color)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          minWidth: 0,
        }}
      >
        <Group gap={8} wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Group gap={6} wrap="nowrap" align="baseline" style={{ flexShrink: 0 }}>
            <Text fw={600} size="xs" component="span" lh={1.2}>
              Переписка
            </Text>
            <Text size="xs" c="dimmed" component="span" lh={1.2}>
              {messagesTotal} сообщ.
            </Text>
          </Group>
          <TicketChatHeaderTagsRow
            key={ticket.id}
            ticketTagIds={ticket.tag_ids || []}
            tags={tags}
            tagsToAdd={tagsToAdd}
            onRemoveTag={onRemoveTag}
            onAddTag={onAddTag}
          />
        </Group>
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          <TicketSpamControl ticket={ticket} onChange={onSpamChange} />
          <Tooltip
            label={
              showAssignmentSystemMessages
                ? 'Скрыть системные (назначение, авто, перераспределение, статус)'
                : 'Показать системные (назначение, авто, перераспределение, статус)'
            }
          >
            <ActionIcon
              variant={showAssignmentSystemMessages ? 'light' : 'subtle'}
              color={showAssignmentSystemMessages ? 'teal' : undefined}
              size="sm"
              aria-label={
                showAssignmentSystemMessages
                  ? 'Скрыть системные (назначение, авто, перераспределение, статус)'
                  : 'Показать системные (назначение, авто, перераспределение, статус)'
              }
              aria-pressed={showAssignmentSystemMessages}
              loading={assignmentTimelineLoading && showAssignmentSystemMessages}
              onClick={onToggleSystemMessages}
            >
              {showAssignmentSystemMessages ? (
                <IconEye size={16} stroke={1.5} />
              ) : (
                <IconEyeOff size={16} stroke={1.5} />
              )}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Таймлайн обращения">
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="Открыть таймлайн обращения"
              loading={timelineLoading}
              onClick={onOpenTimeline}
            >
              <IconHistory size={16} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Открыть это обращение в новой вкладке браузера">
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="Открыть обращение в новой вкладке браузера"
              onClick={onOpenTicketInNewTab}
            >
              <IconExternalLink size={16} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Обновить переписку">
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="Обновить переписку"
              loading={loading || threadRefreshing}
              onClick={onRefreshThread}
            >
              <IconRefresh size={16} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Box>
      <Box
        ref={scrollViewport}
        className="ticket-messages-scroll"
        style={{
          flex: isPhoneLayout ? '0 0 auto' : '1 1 0%',
          minHeight: 0,
          ...(isPhoneLayout
            ? { minHeight: 'min(36dvh, 300px)' }
            : !isTicketWideLayout
              ? { minHeight: 'min(30dvh, 260px)' }
              : {}),
          overflowY: isPhoneLayout ? 'visible' : 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: isPhoneLayout ? undefined : 'touch',
        }}
      >
        <Stack gap="xs" p="sm">
          {messages.length < messagesTotal ? (
            <Button variant="light" size="compact-xs" onClick={onLoadOlder}>
              Загрузить раньше
            </Button>
          ) : null}
          {showAssignmentSystemMessages ? (
            assignmentTimelineLoading ? (
              <Text size="xs" c="dimmed">
                Загрузка системных событий таймлайна...
              </Text>
            ) : assignmentTimelineError ? (
              <Alert color="red" variant="light" p="xs">
                <Text size="xs">{assignmentTimelineError}</Text>
              </Alert>
            ) : assignmentTimelineItems.length === 0 ? (
              <Alert color="gray" variant="light" p="xs">
                <Text size="xs">В таймлайне нет системных событий для отображения в переписке.</Text>
              </Alert>
            ) : (
              <Text size="xs" c="dimmed">
                Показаны события таймлайна: {assignmentTimelineItems.length}
              </Text>
            )
          ) : null}
          {String(ticket.channel_type || '').toLowerCase() === 'widget' && widgetEmailWait.active ? (
            <Alert color="blue" variant="light" p="xs" icon={<IconInbox size={18} stroke={1.5} />}>
              <Text size="xs" fw={600}>
                Клиент ждёт ответ на электронную почту
              </Text>
              {widgetEmailWait.replyTo ? (
                <Text size="xs" c="dimmed" mt={4}>
                  Адрес: {widgetEmailWait.replyTo}
                </Text>
              ) : null}
              <Text size="xs" c="dimmed" mt={4}>
                Ниже нажмите «Ответить на почту» — этот же текст уйдёт письмом через email-канал проекта на указанный адрес.
              </Text>
            </Alert>
          ) : null}
          {visibleMessages.map((message) => (
            <MessageBubble
              key={message.id}
              m={message}
              currentUserId={currentUserId}
              users={users}
              quoted={resolveQuotedForMessage(message, messageById)}
              onQuote={
                ticket.status === 'closed' || isAssignmentSystemPseudoMessage(message)
                  ? undefined
                  : onQuoteMessage
              }
              showTranslation={Boolean(ticket.translation_enabled)}
              translationEnabled={Boolean(ticket.translation_enabled)}
            />
          ))}
          <div ref={messagesEndRef} />
        </Stack>
      </Box>
    </Card>
  )
}
